/**
 * Intersection Observer 훅
 *
 * 주요 기능:
 * - 요소의 뷰포트 진입 여부 감지
 * - 스크롤 애니메이션 및 지연 로딩(Lazy Loading)에 활용
 * - 커스터마이징 가능한 threshold 및 rootMargin 옵션
 *
 * 옵션:
 * - threshold: 요소가 보이는 것으로 간주되는 비율 (기본값: 0.1 = 10%)
 * - rootMargin: 루트 요소의 마진 (기본값: '0px')
 * - triggerOnce: 한 번만 트리거할지 여부 (기본값: true)
 *
 * 동작 방식:
 * - 요소가 뷰포트에 진입하면 isInView를 true로 설정
 * - triggerOnce가 true면 진입 후 observer 해제
 * - triggerOnce가 false면 진입/이탈 시 isInView 토글
 *
 * 사용처:
 * - 스크롤 애니메이션 (MusicCardUnified 등)
 * - 이미지 지연 로딩
 * - 무한 스크롤 페이지네이션
 */

import { useEffect, useRef, useState } from "react";

interface UseIntersectionObserverProps {
  /** 요소가 보이는 것으로 간주되는 비율 (0.0 ~ 1.0) */
  threshold?: number;
  /** 루트 요소의 마진 */
  rootMargin?: string;
  /** 한 번만 트리거할지 여부 */
  triggerOnce?: boolean;
}

/**
 * Intersection Observer API를 활용한 뷰포트 진입 감지 훅
 *
 * @param threshold - 요소가 보이는 것으로 간주되는 비율 (기본값: 0.1)
 * @param rootMargin - 루트 요소의 마진 (기본값: '0px')
 * @param triggerOnce - 한 번만 트리거할지 여부 (기본값: true)
 * @returns ref와 isInView 상태
 */
export const useIntersectionObserver = ({
  threshold = 0.1,
  rootMargin = "0px",
  triggerOnce = true,
}: UseIntersectionObserverProps = {}) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // 한 번만 트리거하는 경우 observer 해제
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          // 반복 트리거하는 경우 이탈 시 false로 설정
          setIsInView(false);
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isInView };
};
